import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addAccountUser,
  listAccountUsers,
  updateAccountUserRole,
} from "../../api/endpoints/accountPermissions.js";
import { filterBySearch } from "../services/entityFilters.js";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 500];

export function useAccountPermissions(accountId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const reload = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listAccountUsers(accountId);
      setItems(data);
    } catch (err) {
      setError(err.message ?? "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filteredItems = useMemo(
    () => filterBySearch(items, search, ["name", "email"]),
    [items, search],
  );

  const total = filteredItems.length;
  const totalAll = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  const changeRole = useCallback(async (userId, role) => {
    if (!accountId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateAccountUserRole(accountId, userId, role);
      setItems((prev) => prev.map((u) => (u.id === userId ? updated : u)));
    } catch (err) {
      setError(err.message ?? "Failed to update role");
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const addUser = useCallback(async (payload) => {
    if (!accountId) return;
    setSaving(true);
    setError(null);
    try {
      const created = await addAccountUser(accountId, payload);
      setItems((prev) => [...prev, created]);
      setAddOpen(false);
      const newTotal = items.length + 1;
      const lastPage = Math.max(1, Math.ceil(newTotal / pageSize));
      setPage(lastPage);
    } catch (err) {
      setError(err.message ?? "Failed to add user");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId, items.length, pageSize]);

  const setPageSizeSafe = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);

  return {
    items: pagedItems,
    allItems: items,
    filteredCount: total,
    loading,
    saving,
    error,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize: setPageSizeSafe,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    total,
    totalAll,
    totalPages,
    rangeStart,
    rangeEnd,
    addOpen,
    setAddOpen,
    changeRole,
    addUser,
    reload,
  };
}
